// Catalog.ID Client — Binary QR → SVG
// ============================================================================
// Renders a binary payload as a QR code SVG string for embedding in pdfmake
// documents (its built-in `qr` content type only accepts strings, but
// Recovery Sheets use binary byte-mode segments per Addendum §6.2).
//
// Defaults follow §6.2: error correction level H (~30 % redundancy) and a
// 4-module quiet zone.

import * as qrcodegenNs from 'nayuki-qr-code-generator';
import type QrCodegenTypes from 'nayuki-qr-code-generator';

// CJS/ESM interop varies by bundler: Vite honors the package's `__esModule`
// marker (namespace.default = inner exports) while node-mode esbuild maps
// `default` to module.exports itself. Resolve whichever layer has QrCode.
const qrcodegen: typeof QrCodegenTypes = (() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let m: any = qrcodegenNs;
    while (m && !m.QrCode && m.default) m = m.default;
    return m;
})();

export interface QrSvgOptions {
    /** Quiet-zone width in modules. QR spec minimum is 4. */
    border?: number;
    /** Error correction level; archival sheets use 'H'. */
    ecc?: 'L' | 'M' | 'Q' | 'H';
}

const ECC = {
    L: qrcodegen.QrCode.Ecc.LOW,
    M: qrcodegen.QrCode.Ecc.MEDIUM,
    Q: qrcodegen.QrCode.Ecc.QUARTILE,
    H: qrcodegen.QrCode.Ecc.HIGH,
} as const;

/**
 * Encode `data` as a byte-mode QR and return an SVG string. The SVG viewBox
 * is in module units; scale via width/height where it is embedded.
 */
export function qrToSvg(data: Uint8Array, options: QrSvgOptions = {}): string {
    const border = options.border ?? 4;
    const qr = qrcodegen.QrCode.encodeSegments(
        [qrcodegen.QrSegment.makeBytes(Array.from(data))],
        ECC[options.ecc ?? 'H'],
    );

    const parts: string[] = [];
    for (let y = 0; y < qr.size; y++) {
        for (let x = 0; x < qr.size; x++) {
            if (qr.getModule(x, y)) {
                parts.push(`M${x + border},${y + border}h1v1h-1z`);
            }
        }
    }
    const dim = qr.size + border * 2;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" shape-rendering="crispEdges">`
        + `<rect width="${dim}" height="${dim}" fill="#ffffff"/>`
        + `<path d="${parts.join('')}" fill="#000000"/>`
        + `</svg>`;
}

/** Module count (including quiet zone) for sizing decisions. */
export function qrModuleCount(data: Uint8Array, options: QrSvgOptions = {}): number {
    const border = options.border ?? 4;
    const qr = qrcodegen.QrCode.encodeSegments(
        [qrcodegen.QrSegment.makeBytes(Array.from(data))],
        ECC[options.ecc ?? 'H'],
    );
    return qr.size + border * 2;
}
